import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';
import { deriveEmotionsReportRow } from '@peekapak/types/EmotionsReportRow';
import type { EmotionsReportStudent } from '@peekapak/types/EmotionsReportRow';
import type { UrgencyTier } from '@peekapak/types/EmotionsClassification';
import Button from '../../components/Button';
import { getConcerningEmotionsReport } from '../../BackendInterface';
import type { GradeBand } from '../WeeklyClassSnapshot/WeeklyClassSnapshot';
import styles from './StudentsWhoMayNeedSupport.module.scss';

const LEVELS: Record<GradeBand, { tier: UrgencyTier; label: string }[]> = {
  elementary: [{ tier: 'emerging', label: 'Emerging' }],
  secondary: [
    { tier: 'urgent', label: 'Urgent' },
    { tier: 'concerning', label: 'Concerning' },
    { tier: 'emerging', label: 'Emerging' },
  ],
};

interface StudentsWhoMayNeedSupportProps {
  classroomId: string;
  gradeBand: GradeBand;
}

const StudentsWhoMayNeedSupport = ({ classroomId, gradeBand }: StudentsWhoMayNeedSupportProps) => {
  const history = useHistory();
  const [students, setStudents] = useState<EmotionsReportStudent[]>();

  useEffect(() => {
    let canceled = false;
    getConcerningEmotionsReport('own')
      .then((classrooms) => {
        if (!canceled) setStudents(classrooms.find((c) => c.classroomId === classroomId)?.students ?? []);
      })
      // Without the report data there is nothing to show, so the section stays hidden
      .catch(() => undefined);
    return () => {
      canceled = true;
    };
  }, [classroomId]);

  if (!students) return null;

  const now = Date.now();
  const tiers = students.map(
    (student) =>
      deriveEmotionsReportRow(
        student.checkins,
        gradeBand === 'elementary' ? 'PK-5' : '6-12',
        student.emotionsUrgency,
        { start: now, end: now },
        now,
      ).urgencyTier,
  );
  const levels = LEVELS[gradeBand].map((level) => ({
    ...level,
    count: tiers.filter((tier) => tier === level.tier).length,
  }));

  return (
    <section className={styles.section}>
      <h2>Students who may need support</h2>
      <p className={styles.subtitle}>
        Based on each student&apos;s recent pattern of check-ins. This number does not change when you move between
        weeks above.
      </p>
      <div className={cx(styles.callout, styles[gradeBand])}>
        <span className={styles.count} data-testid='support-count'>
          {levels.reduce((sum, level) => sum + level.count, 0)}
        </span>
        <div className={styles.message}>
          <p>
            <b>students</b> have a recent pattern of check-ins that may need your attention.
          </p>
          <div className={styles.levels}>
            {levels.map((level) => (
              <span key={level.tier} className={styles[level.tier]}>
                {level.label}: {level.count}
              </span>
            ))}
          </div>
        </div>
        <Button
          small
          darkBlue
          className={styles.reportButton}
          onClick={() => history.push('/reports/ConcerningEmotions')}
        >
          Open Concerning Emotions Report ›
        </Button>
      </div>
    </section>
  );
};

export default StudentsWhoMayNeedSupport;
